import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn, getRoleThemeClass, getRoleName } from '../../lib/utils';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  LayoutDashboard, Calendar, Users, Home, FileText, AlertTriangle,
  BookOpen, Clock, Settings, LogOut, Moon, Sun, Shield, ClipboardList,
  Bell, MessageSquare, Menu, X, ChevronRight, Archive
} from 'lucide-react';

const LOGO_URL = '/logo-small.png';

// Hook to detect mobile/tablet
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return { isMobile, isTablet, isDesktop: !isMobile && !isTablet };
};

const MobileBottomNav = ({ items, basePath }) => {
  const location = useLocation();
  
  // Priority items for bottom nav (max 5)
  const priorityItems = items.slice(0, 5);
  
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {priorityItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path !== basePath && location.pathname.startsWith(item.path));
          
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center justify-center min-w-[64px] h-14 px-2 rounded-lg transition-all",
                "active:scale-95 touch-manipulation",
                isActive 
                  ? "text-primary bg-primary/10" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className={cn("h-5 w-5 mb-1", isActive && "text-primary")} />
              <span className="text-[10px] font-medium truncate max-w-[60px]">
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

const MobileSidebar = ({ items, isOpen, onClose, user, onLogout, theme, toggleTheme }) => {
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="left" className="w-[280px] p-0">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <img src={LOGO_URL} alt="Logo" className="h-10 object-contain" />
            <Button variant="ghost" size="icon" onClick={() => onClose(false)}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* User Info */}
          <div className="p-4 border-b bg-muted/30">
            <p className="font-semibold">{user?.first_name} {user?.last_name}</p>
            <p className="text-sm text-muted-foreground">{getRoleName(user?.role)}</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => onClose(false)}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-all",
                    "touch-manipulation active:scale-[0.98]",
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                  <ChevronRight className="h-4 w-4 ml-auto opacity-50" />
                </NavLink>
              );
            })}
          </nav>

          {/* Footer Actions */}
          <div className="p-4 border-t space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3"
              onClick={toggleTheme}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-destructive hover:text-destructive"
              onClick={onLogout}
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const MobileHeader = ({ onMenuOpen, title, user }) => {
  return (
    <header className="sticky top-0 z-40 flex items-center justify-between h-14 px-4 bg-background/95 backdrop-blur-lg border-b border-border safe-area-top">
      <Button variant="ghost" size="icon" onClick={onMenuOpen} className="touch-manipulation">
        <Menu className="h-5 w-5" />
      </Button>
      
      <div className="flex-1 text-center">
        <h1 className="font-semibold text-sm truncate">{title || 'Dashboard'}</h1>
      </div>
      
      <div className="flex items-center gap-2">
        <NavLink to={`/${user?.role === 'house_manager' ? 'manager' : user?.role}/notifications`}>
          <Button variant="ghost" size="icon" className="touch-manipulation">
            <Bell className="h-5 w-5" />
          </Button>
        </NavLink>
      </div>
    </header>
  );
};

const ResponsiveSidebar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = {
    admin: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
      { icon: Calendar, label: 'Roster', path: '/admin/roster' },
      { icon: Users, label: 'Participants', path: '/admin/participants' },
      { icon: Home, label: 'Houses', path: '/admin/houses' },
      { icon: FileText, label: 'Shift Notes', path: '/admin/notes' },
      { icon: FileText, label: 'Timesheets', path: '/admin/timesheets' },
      { icon: AlertTriangle, label: 'Incidents', path: '/admin/incidents' },
      { icon: BookOpen, label: 'Daily Diary', path: '/admin/diary' },
      { icon: MessageSquare, label: 'Communications', path: '/admin/communications' },
      { icon: Clock, label: 'Leave Requests', path: '/admin/leave' },
      { icon: Shield, label: 'Users', path: '/admin/users' },
      { icon: ClipboardList, label: 'Compliance', path: '/admin/compliance' },
      { icon: Archive, label: 'Archived', path: '/admin/archived' },
      { icon: Bell, label: 'Notifications', path: '/admin/notifications' },
      { icon: ClipboardList, label: 'Audit Logs', path: '/admin/audit' },
      { icon: Settings, label: 'Settings', path: '/admin/settings' },
    ],
    house_manager: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/manager' },
      { icon: Calendar, label: 'Roster', path: '/manager/roster' },
      { icon: Users, label: 'My Team', path: '/manager/team' },
      { icon: Users, label: 'Participants', path: '/manager/participants' },
      { icon: FileText, label: 'Shift Notes', path: '/manager/notes' },
      { icon: FileText, label: 'Timesheets', path: '/manager/timesheets' },
      { icon: AlertTriangle, label: 'Incidents', path: '/manager/incidents' },
      { icon: BookOpen, label: 'Daily Diary', path: '/manager/diary' },
      { icon: MessageSquare, label: 'Communications', path: '/manager/communications' },
      { icon: Clock, label: 'Leave Requests', path: '/manager/leave' },
      { icon: Archive, label: 'Archived', path: '/manager/archived' },
      { icon: Bell, label: 'Notifications', path: '/manager/notifications' },
      { icon: Settings, label: 'Settings', path: '/manager/settings' },
    ],
    worker: [
      { icon: LayoutDashboard, label: 'Home', path: '/worker' },
      { icon: Calendar, label: 'Roster', path: '/worker/roster' },
      { icon: FileText, label: 'Notes', path: '/worker/notes' },
      { icon: BookOpen, label: 'Diary', path: '/worker/diary' },
      { icon: Users, label: 'Clients', path: '/worker/clients' },
      { icon: Users, label: 'Team Roster', path: '/worker/team' },
      { icon: FileText, label: 'Team Notes', path: '/worker/team-notes' },
      { icon: AlertTriangle, label: 'Incidents', path: '/worker/incidents' },
      { icon: MessageSquare, label: 'Communications', path: '/worker/communications' },
      { icon: Clock, label: 'Leave', path: '/worker/leave' },
      { icon: Shield, label: 'My Profile', path: '/worker/profile' },
      { icon: Bell, label: 'Notifications', path: '/worker/notifications' },
      { icon: Settings, label: 'Settings', path: '/worker/settings' },
    ],
    family: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/family' },
      { icon: BookOpen, label: 'Daily Diary', path: '/family/diary' },
      { icon: AlertTriangle, label: 'Incidents', path: '/family/incidents' },
      { icon: Bell, label: 'Notifications', path: '/family/notifications' },
      { icon: Settings, label: 'Settings', path: '/family/settings' },
    ]
  };

  const items = navItems[user?.role] || [];
  const basePath = `/${user?.role === 'house_manager' ? 'manager' : user?.role}`;

  // Get current page title
  const currentItem = items.find(item => 
    location.pathname === item.path || 
    (item.path !== basePath && location.pathname.startsWith(item.path))
  );
  const pageTitle = currentItem?.label || 'Dashboard';

  // Mobile layout
  if (isMobile) {
    return (
      <>
        <MobileHeader 
          onMenuOpen={() => setSidebarOpen(true)} 
          title={pageTitle}
          user={user}
        />
        <MobileSidebar
          items={items}
          isOpen={sidebarOpen}
          onClose={setSidebarOpen}
          user={user}
          onLogout={handleLogout}
          theme={theme}
          toggleTheme={toggleTheme}
        />
        <MobileBottomNav items={items} basePath={basePath} />
      </>
    );
  }

  // Tablet - Collapsible sidebar
  if (isTablet) {
    return (
      <>
        <MobileHeader 
          onMenuOpen={() => setSidebarOpen(true)} 
          title={pageTitle}
          user={user}
        />
        <MobileSidebar
          items={items}
          isOpen={sidebarOpen}
          onClose={setSidebarOpen}
          user={user}
          onLogout={handleLogout}
          theme={theme}
          toggleTheme={toggleTheme}
        />
      </>
    );
  }

  // Desktop - Full sidebar (original)
  return (
    <aside className={cn(
      "fixed left-0 top-0 z-40 h-screen w-64 border-r border-border/40 bg-card/50 backdrop-blur-xl transition-transform",
      getRoleThemeClass(user?.role)
    )}>
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-20 items-center justify-center border-b border-border/40 px-4">
          <img src={LOGO_URL} alt="Limitless Community Logo" className="h-14 object-contain" />
        </div>

        {/* User Info */}
        <div className="px-4 py-4 border-b border-border/40">
          <p className="text-sm font-semibold">{user?.first_name} {user?.last_name}</p>
          <p className="text-xs text-muted-foreground">{getRoleName(user?.role)}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border/40 space-y-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={toggleTheme}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {theme === 'dark' ? 'Light' : 'Dark'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-destructive hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </div>
    </aside>
  );
};

export default ResponsiveSidebar;
